import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';
import { StructuredData } from '../components/StructuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language, localizedPath } = useSiteContent();
  
  // Set page title
  usePageTitle('faq');

  // Default translations (fallback)
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'ЧЗВ',
    heroTitle: 'Често задавани',
    heroTitleHighlight: 'въпроси',
    heroDescription: 'Намерете отговори на често задавани въпроси за нашите услуги, цени и процеси.',
    stillHaveQuestions: 'Все още имате въпроси?',
    supportText: 'Нашият екип за клиентска поддръжка е тук, за да ви помогне с всякакви въпроси.',
    contactSupport: 'Свържете се с поддръжка',
    readyToStart: 'Готови да започнете?',
    quoteText: 'Заявете персонализирана оферта за вашите транспортни нужди.',
    requestQuote: 'Заявете оферта',
    quickLinks: 'Бързи връзки',
    quickLinksText: 'Разгледайте повече за нашите услуги',
    linkLabels: { 'Services': 'Услуги', 'Fleet': 'Флот', 'Destinations': 'Дестинации', 'About Us': 'За нас' }
  } : {
    heroSubtitle: 'FAQ',
    heroTitle: 'Frequently Asked',
    heroTitleHighlight: 'Questions',
    heroDescription: 'Find answers to common questions about our services, pricing, and processes.',
    stillHaveQuestions: 'Still Have Questions?',
    supportText: 'Our customer support team is here to help you with any questions.',
    contactSupport: 'Contact Support',
    readyToStart: 'Ready to Get Started?',
    quoteText: 'Request a personalized quote for your transport needs.',
    requestQuote: 'Request Quote',
    quickLinks: 'Quick Links',
    quickLinksText: 'Explore more about our services',
    linkLabels: { 'Services': 'Services', 'Fleet': 'Fleet', 'Destinations': 'Destinations', 'About Us': 'About Us' }
  };

  // Build content from API or defaults
  const content = pageContent ? {
    heroSubtitle: language === 'bg' ? (pageContent.hero_subtitle_bg || defaultContent.heroSubtitle) : (pageContent.hero_subtitle_en || defaultContent.heroSubtitle),
    heroTitle: language === 'bg' ? (pageContent.hero_title_bg || defaultContent.heroTitle) : (pageContent.hero_title_en || defaultContent.heroTitle),
    heroTitleHighlight: language === 'bg' ? (pageContent.hero_title_highlight_bg || defaultContent.heroTitleHighlight) : (pageContent.hero_title_highlight_en || defaultContent.heroTitleHighlight),
    heroDescription: language === 'bg' ? (pageContent.hero_description_bg || defaultContent.heroDescription) : (pageContent.hero_description_en || defaultContent.heroDescription),
    stillHaveQuestions: language === 'bg' ? (pageContent.still_have_questions_bg || defaultContent.stillHaveQuestions) : (pageContent.still_have_questions_en || defaultContent.stillHaveQuestions),
    supportText: language === 'bg' ? (pageContent.support_text_bg || defaultContent.supportText) : (pageContent.support_text_en || defaultContent.supportText),
    contactSupport: language === 'bg' ? (pageContent.contact_support_bg || defaultContent.contactSupport) : (pageContent.contact_support_en || defaultContent.contactSupport),
    readyToStart: language === 'bg' ? (pageContent.ready_to_start_bg || defaultContent.readyToStart) : (pageContent.ready_to_start_en || defaultContent.readyToStart),
    quoteText: language === 'bg' ? (pageContent.quote_text_bg || defaultContent.quoteText) : (pageContent.quote_text_en || defaultContent.quoteText),
    requestQuote: language === 'bg' ? (pageContent.request_quote_bg || defaultContent.requestQuote) : (pageContent.request_quote_en || defaultContent.requestQuote),
    quickLinks: defaultContent.quickLinks,
    quickLinksText: defaultContent.quickLinksText,
    linkLabels: defaultContent.linkLabels
  } : defaultContent;

  useEffect(() => {
    const fetchData = async () => { 
      try { 
        const [faqRes, contentRes] = await Promise.all([
          axios.get(`${API}/faq`),
          axios.get(`${API}/faq-page-content`)
        ]);
        setFaqs(faqRes.data.faqs);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) { 
        console.error('Error:', error); 
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Prepare FAQ data for structured data
  const faqSchemaData = faqs.map(faq => ({
    question: language === 'bg' && faq.question_bg ? faq.question_bg : (faq.question_en || faq.question),
    answer: language === 'bg' && faq.answer_bg ? faq.answer_bg : (faq.answer_en || faq.answer)
  }));

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="faq-page" className="pt-20 bg-slate-50">
      <SEO page="faq" />
      {faqSchemaData.length > 0 && <StructuredData type="faq" data={faqSchemaData} />}
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section data-testid="faq-section" className="section-padding bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => {
              const question = language === 'bg' && faq.question_bg ? faq.question_bg : (faq.question_en || faq.question);
              const answer = language === 'bg' && faq.answer_bg ? faq.answer_bg : (faq.answer_en || faq.answer);
              return (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  data-testid={`faq-item-${index}`} 
                  className="bg-white border border-slate-100 rounded-2xl px-6 shadow-sm animate-fade-in-up overflow-hidden transition-all duration-300 hover:shadow-md hover:border-slate-200" 
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6 transition-all duration-300 [&[data-state=open]]:pb-2">
                    <div className="flex items-start gap-4">
                      <HelpCircle className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-900 font-bold">{question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pl-9 text-slate-500 leading-relaxed">{answer}</AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Additional Questions */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 border-slate-100 p-8 rounded-3xl">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{content.stillHaveQuestions}</h3>
              <p className="text-slate-500 mb-6">{content.supportText}</p>
              <Link to={localizedPath('/contact')}>
                <button data-testid="faq-contact-btn" className="btn-modern btn-primary btn-shimmer">
                  {content.contactSupport} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
            <div className="bg-slate-50 border-slate-100 p-8 rounded-3xl">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{content.readyToStart}</h3>
              <p className="text-slate-500 mb-6">{content.quoteText}</p>
              <Link to={`${localizedPath('/contact')}?quote=true`}>
                <button data-testid="faq-quote-btn" className="btn-modern btn-secondary">
                  {content.requestQuote} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <h3 className="text-xl font-bold text-slate-900 mb-2">{content.quickLinks}</h3>
            <p className="text-slate-500 text-sm">{content.quickLinksText}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { key: 'Services', path: '/services' },
              { key: 'Fleet', path: '/fleet' },
              { key: 'Destinations', path: '/destinations' },
              { key: 'About Us', path: '/about' }
            ].map((link) => (
              <Link 
                key={link.key} 
                to={localizedPath(link.path)} 
                className="px-4 py-2 bg-white rounded-full text-sm text-slate-600 hover:bg-[#C41E3A] hover:text-white transition-colors shadow-sm"
              >
                {content.linkLabels[link.key]}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
